import type { InternalAxiosRequestConfig } from 'axios';
import axios, { AxiosStatusCode } from '../axios';
import * as html from '../html';
import type Modal from '../modal';
import * as accounts from './accounts';
import * as session from './session';

// Token this page's content was rendered under. Storage events compare the
// live token against this so a background tab knows its painted state is stale.
let pageToken: string | null = null;
// userID behind pageToken, resolved from the stored account list. Token
// strings rotate on every session create AND Remember replaces the stored
// string per userID, so the userID is the only durable identity — a frozen
// tab whose account comes back under a rotated token can only recognize it
// by userID once Remember has overwritten the old string.
let pageUserID: string | null = null;
let dirty = false;
let frozen = false;
// True while switchBack is mid-flight so the interceptor doesn't stall the
// Switch request that is unfreezing us.
let restoring = false;
let modal: Modal | undefined;
// Fingerprint of the open freeze modal's kind + resolved names, so a
// re-evaluation (late 'accounts' Remember) only rebuilds it when the
// displayed names actually changed.
let modalState: string | undefined;
let onSoftChange: (() => void) | undefined;

// The stored entry for the account this page was painted for — by userID
// when known (survives token rotation + Remember overwrites), else by the
// original token string.
function findPageEntry(): accounts.StoredAccount | undefined {
    const list = accounts.List();
    if (pageUserID !== null) {
        return list.find(e => e.userID === pageUserID);
    }
    if (pageToken !== null) {
        return list.find(e => e.token === pageToken);
    }
    return undefined;
}

function sameAsPage(live: string | null): boolean {
    if (live === pageToken) {
        return true;
    }
    if (live === null || pageToken === null) {
        return false;
    }
    const liveEntry = accounts.List().find(e => e.token === live);
    if (!liveEntry) {
        return false;
    }
    if (pageUserID !== null) {
        return liveEntry.userID === pageUserID;
    }
    const pageEntry = findPageEntry();
    if (!pageEntry) {
        return false;
    }
    return pageEntry.userID === liveEntry.userID;
}

// Adopt live as the page's account: its state is (or just became) valid for it.
function adoptToken(live: string | null) {
    pageToken = live;
    if (live === null) {
        pageUserID = null;
        return;
    }
    const entry = accounts.List().find(e => e.token === live);
    if (entry) {
        pageUserID = entry.userID;
    }
}

function entryDisplayName(entry: accounts.StoredAccount | undefined, fallback: string): string {
    if (!entry) {
        return fallback;
    }
    return entry.name || entry.email || fallback;
}

// Method bag so tests can spyOn(reloadTab, 'run') — jsdom's window.location is
// non-configurable, so Object.defineProperty(window, 'location', …) throws.
export const reloadTab = {
    run() {
        window.location.reload();
    },
};

function freezeAndReload() {
    frozen = true;
    reloadTab.run();
}

async function switchBack(oldEntry: accounts.StoredAccount) {
    restoring = true;
    try {
        await accounts.Switch(oldEntry);
        pageToken = session.GetLoginToken();
        pageUserID = oldEntry.userID;
        frozen = false;
        modal?.close();
        modal = undefined;
        modalState = undefined;
        onSoftChange?.();
    } catch (err: unknown) {
        if (AxiosStatusCode(err) === 404) {
            // Switch already evicted the dead entry; drop the switch-back
            // button so the user can only reload.
            modal?.$element.querySelectorAll('button.btn-outline-secondary').forEach($btn => $btn.remove());
            modal?.showError("That account's session has expired — reload to continue.");
        } else {
            const message = err instanceof Error ? err.message : String(err);
            modal?.showError(message);
        }
    } finally {
        restoring = false;
    }
}

async function showFreezeModal(kind: 'switched' | 'logged-out', live: string | null) {
    frozen = true;

    const oldEntry = findPageEntry();
    const oldName = entryDisplayName(oldEntry, 'another account');
    const newName = entryDisplayName(live === null ? undefined : accounts.List().find(e => e.token === live), 'your other account');
    const escapedOld = html.Escape(oldName);
    const escapedNew = html.Escape(newName);

    // Re-evaluations (stacked switches, late 'accounts' Remembers) land here
    // repeatedly; only rebuild when the displayed names actually changed.
    const state = `${kind}|${oldName}|${newName}`;
    if (modal && state === modalState) {
        return;
    }
    modalState = state;

    const { default: Modal } = await import('../modal');

    // Replace any previous freeze modal so names stay current.
    if (modal) {
        modal.close();
        modal = undefined;
    }

    if (kind === 'logged-out') {
        modal = new Modal({
            title: 'Logged out in another tab',
            body: `You logged out in another tab, so this page can no longer be used with <strong>${escapedOld}</strong>'s account. Reload to continue.`,
            close: false,
            escape: false,
            outsideClosable: false,
            confirmText: 'Reload',
            onSubmit: () => {
                reloadTab.run();
            },
        });
        return;
    }

    // switched
    let body = `This browser is now signed in as <strong>${escapedNew}</strong>, but this page is still showing <strong>${escapedOld}</strong>'s information.`;
    const buttons: { text: string; class: string; click: (e: MouseEvent) => void }[] = [];

    if (oldEntry) {
        body += ` Reload to continue as ${escapedNew}, or switch back to finish what you were doing here.`;
        buttons.push({
            text: `Switch back to ${oldName}`,
            class: 'btn btn-outline-secondary',
            click: () => {
                void switchBack(oldEntry);
            },
        });
    } else {
        body += ` Reload to continue as ${escapedNew}.`;
    }

    modal = new Modal({
        title: 'Account switched in another tab',
        body,
        close: false,
        escape: false,
        outsideClosable: false,
        confirmText: 'Reload',
        buttons: buttons.length ? buttons : undefined,
        onSubmit: () => {
            reloadTab.run();
        },
    });
}

function onStorage(e: StorageEvent) {
    if (e.key !== 'loginToken' && e.key !== 'accounts') {
        return;
    }
    // Token strings rotate on every session create (fresh EncryptLoginToken
    // IV), and the switching tab Remembers the fresh token only after its own
    // session resolves — moments AFTER the loginToken event. Re-evaluating on
    // that late 'accounts' write is what lets a frozen tab name the new
    // account, or discover the live token is really the account it was
    // painted for. Only while frozen: other accounts-list churn is noise.
    if (e.key === 'accounts' && !frozen) {
        return;
    }

    // The page's own entry may not have been resolvable at Init (first-ever
    // login on this browser); pick up the userID as soon as it appears.
    if (pageUserID === null && pageToken !== null) {
        pageUserID = accounts.List().find(en => en.token === pageToken)?.userID ?? null;
    }

    // Always read the live token at handler time — events can arrive stacked.
    const live = session.GetLoginToken();

    if (sameAsPage(live)) {
        if (frozen) {
            // Other tab switched back to the account this page was painted for.
            adoptToken(live);
            frozen = false;
            modal?.close();
            modal = undefined;
            modalState = undefined;
            onSoftChange?.();
            return;
        }
        if (live !== pageToken) {
            // Same account, token string rotated (fresh EncryptLoginToken IV).
            adoptToken(live);
            onSoftChange?.();
        }
        return;
    }

    // Login elsewhere: was logged out, now has a token.
    if (pageToken === null && live !== null) {
        if (!dirty) {
            freezeAndReload();
            return;
        }
        // Logged-out page holds no account-prefilled data; keep soft header repaint.
        adoptToken(live);
        onSoftChange?.();
        return;
    }

    // Logout elsewhere.
    if (pageToken !== null && live === null) {
        if (!dirty) {
            freezeAndReload();
            return;
        }
        void showFreezeModal('logged-out', live);
        return;
    }

    // Different account (both non-null, not same account).
    if (pageToken !== null && live !== null) {
        if (!dirty) {
            freezeAndReload();
            return;
        }
        void showFreezeModal('switched', live);
    }
}

// Teardown lives on window (like smg/modal's defaults) so a second Init —
// another module instance from esbuild code splitting, or a fresh import in
// tests — can unhook the previous instance's listeners instead of leaving a
// zombie guard reacting to storage events with stale state.
declare interface guardWindow extends Window {
    __smgSessionGuardTeardown?: () => void;
}
declare const window: guardWindow;

export function Init(opts: { onSoftChange: () => void }) {
    window.__smgSessionGuardTeardown?.();

    onSoftChange = opts.onSoftChange;
    pageToken = session.GetLoginToken();
    pageUserID = pageToken === null ? null : accounts.List().find(e => e.token === pageToken)?.userID ?? null;
    dirty = false;
    frozen = false;
    restoring = false;
    modal = undefined;
    modalState = undefined;

    // Dirty tracking — deliberately over-inclusive: worst case a dirty-but-
    // actually-clean tab gets the modal instead of an auto-reload (always safe).
    const markDirty = () => {
        dirty = true;
    };
    const markDirtyOnCanvas = (e: PointerEvent) => {
        if (e.target instanceof Element && e.target.closest('canvas, .konvajs-content')) {
            dirty = true;
        }
    };
    document.addEventListener('input', markDirty, true);
    document.addEventListener('change', markDirty, true);
    document.addEventListener('drop', markDirty, true);
    document.addEventListener('pointerdown', markDirtyOnCanvas, true);

    // Stall every outgoing request while frozen so account-scoped forms can't
    // submit under the wrong account even if the modal overlay is escaped.
    const interceptorID = axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
        if (frozen && !restoring) {
            console.warn('session-guard: blocked request while frozen', config.url);
            // Never-resolving promise: stall, don't reject — a rejection would
            // surface "Network Error" modals behind the freeze modal.
            return new Promise<InternalAxiosRequestConfig>(() => undefined);
        }
        return config;
    });

    window.addEventListener('storage', onStorage);

    window.__smgSessionGuardTeardown = () => {
        document.removeEventListener('input', markDirty, true);
        document.removeEventListener('change', markDirty, true);
        document.removeEventListener('drop', markDirty, true);
        document.removeEventListener('pointerdown', markDirtyOnCanvas, true);
        axios.interceptors.request.eject(interceptorID);
        window.removeEventListener('storage', onStorage);
    };
}
